// Full-document render of the pillows LegalPage: PageSkeleton (the React
// document shell) wrapping the legal-page layout.
import React from "react";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";

import { templates } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { PageSkeleton } from "../components/PageSkeleton";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
export const Template = (
    props: templates.LegalPageTemplate,
): React.JSX.Element => {
    const { page } = props;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div id="legal-page" className="l-capped-width legal-page">
                <div className="content">
                    {page.show_title && <h1>{page.title}</h1>}
                    {page.body ? (
                        <StreamField
                            tagName={React.Fragment}
                            value={page.body.value}
                            components={{
                                // Legacy `{{ self.body }}` rendered each child
                                // through Wagtail StreamBlock.render_basic, which
                                // wraps every block in
                                // `<div class="w-block-{type} block-{type}">`.
                                // The thelabui StreamField does not auto-wrap, so
                                // each dispatcher emits that wrapper itself --
                                // pillows _legal-page.scss keys on
                                // .block-paragraph / .block-image_link / .block-table.
                                heading: (block) => (
                                    <div className="w-block-heading block-heading">
                                        {block.value}
                                    </div>
                                ),
                                paragraph: (block) => (
                                    <div className="w-block-paragraph block-paragraph">
                                        <RichTextBlock value={block.value} />
                                    </div>
                                ),
                                image: (block) => (
                                    <div className="w-block-image block-image">
                                        <img
                                            src={block.value.url}
                                            alt={block.value.default_alt_text}
                                            width={block.value.width}
                                            height={block.value.height}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                ),
                                html: (block) => (
                                    <div className="w-block-html block-html">
                                        <RawHTMLBlock value={block.value} />
                                    </div>
                                ),
                                image_link: (block) => {
                                    const image = block.value.image ? (
                                        <img
                                            src={block.value.image.url}
                                            alt={
                                                block.value.image
                                                    .default_alt_text
                                            }
                                            width={block.value.image.width}
                                            height={block.value.image.height}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    ) : null;
                                    return (
                                        <div className="w-block-image_link block-image_link">
                                            {block.value.page?.url ? (
                                                <a href={block.value.page.url}>
                                                    {image}
                                                </a>
                                            ) : (
                                                image
                                            )}
                                        </div>
                                    );
                                },
                                table: (block) =>
                                    block.value ? (
                                        <div className="w-block-table block-table">
                                            <RawHTMLBlock value={block.value} />
                                        </div>
                                    ) : null,
                            }}
                        />
                    ) : null}
                </div>
            </div>
        </PageSkeleton>
    );
};
